import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, ToggleButton,
  ToggleButtonGroup, Box, Typography, Paper, List, ListItemButton, ListItemText, Checkbox,
  Chip, Alert, CircularProgress, useMediaQuery, useTheme,
} from '@mui/material';
import { Star } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL, REGISTRO_MAS_VENDIDOS, FAVORITOS_GUARDAR } from '../services/apiConfig';

// Diálogo para elegir los favoritos de Registrar Egreso de UNA sucursal. Es un
// componente de nivel superior (y se monta solo mientras está abierto) a
// propósito: definido adentro de RegistroProducto se re-crearía en cada render y
// el buscador perdería el foco en cada tecla.

const MAXIMO = 40; // mismo tope que favoritosguardar.php
const TOP_RAPIDO = 10;

const authHeader = () => {
  const t = sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function FavoritosDialog({ sucursalId, sucursalNombre, productos, favoritosIniciales, onGuardado, onCerrar }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [cargando, setCargando] = useState(true);
  const [errorRanking, setErrorRanking] = useState('');
  const [ranking, setRanking] = useState([]); // [{id_club_grido, descripcion, unidades}]
  // Si no se pueden traer los favoritos frescos se arranca con los que ya conocía
  // la pantalla: así guardar nunca deja la lista vacía por un pedido fallido.
  const [seleccion, setSeleccion] = useState(favoritosIniciales || []);
  const [busqueda, setBusqueda] = useState('');
  const [orden, setOrden] = useState('vendidos');
  const [aviso, setAviso] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [errorGuardar, setErrorGuardar] = useState('');

  // Favoritos guardados HOY + ranking del último mes, en un solo pedido. El
  // flag evita aplicar una respuesta vieja si el diálogo se cierra o StrictMode
  // dispara el efecto dos veces.
  useEffect(() => {
    let cancelado = false;
    axios.get(API_BASE_URL + REGISTRO_MAS_VENDIDOS, { params: { sucursal_id: sucursalId }, headers: authHeader() })
      .then((res) => {
        if (cancelado) return;
        if (!res.data?.success) throw new Error(res.data?.message || 'No se pudo traer lo más vendido.');
        const d = res.data.data ?? {};
        setRanking(Array.isArray(d.ranking) ? d.ranking : []);
        if (Array.isArray(d.favoritos)) setSeleccion(d.favoritos.map(Number));
      })
      .catch((e) => {
        if (!cancelado) setErrorRanking(e.response?.data?.message || e.message || 'No se pudo traer lo más vendido.');
      })
      .finally(() => { if (!cancelado) setCargando(false); });
    return () => { cancelado = true; };
  }, [sucursalId]);

  // Catálogo por id_club_grido: puede haber duplicados, vale la primera
  // ocurrencia; y sin id_club_grido no se puede registrar (agregarProducto lo
  // rechaza), así que esos no se ofrecen.
  const catalogo = useMemo(() => {
    const m = new Map();
    (productos || []).forEach((p) => {
      const id = Number(p.id_club_grido);
      if (id > 0 && !m.has(id)) m.set(id, p);
    });
    return m;
  }, [productos]);

  // Lo que realmente se ve y se guarda: ids que existen en el catálogo (un
  // favorito de un producto ya desactivado no se muestra, así que tampoco se
  // cuenta ni gasta el tope; el servidor igual lo purga al guardar).
  const seleccionVisible = useMemo(
    () => (catalogo.size > 0 ? seleccion.filter((id) => catalogo.has(id)) : seleccion),
    [seleccion, catalogo]
  );

  const unidadesPorId = useMemo(() => {
    const m = new Map();
    ranking.forEach((r) => m.set(Number(r.id_club_grido), Number(r.unidades)));
    return m;
  }, [ranking]);

  const lista = useMemo(() => {
    const t = busqueda.trim().toLowerCase();
    let items = Array.from(catalogo.values());
    if (t) items = items.filter((p) => String(p.art_descripcion || '').toLowerCase().includes(t));
    const porNombre = (a, b) => String(a.art_descripcion || '').localeCompare(String(b.art_descripcion || ''), 'es');
    if (orden === 'az') return items.sort(porNombre);
    return items.sort((a, b) => {
      const ua = unidadesPorId.get(Number(a.id_club_grido)) || 0;
      const ub = unidadesPorId.get(Number(b.id_club_grido)) || 0;
      return ub - ua || porNombre(a, b);
    });
  }, [catalogo, busqueda, orden, unidadesPorId]);

  const alternar = (id) => {
    setAviso('');
    setSeleccion((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      const visibles = catalogo.size > 0 ? prev.filter((x) => catalogo.has(x)).length : prev.length;
      if (visibles >= MAXIMO) {
        setAviso(`Podés elegir hasta ${MAXIMO} favoritos.`);
        return prev;
      }
      return [...prev, id];
    });
  };

  const elegirMasVendidos = () => {
    setAviso('');
    setSeleccion(
      ranking.map((r) => Number(r.id_club_grido)).filter((id) => catalogo.has(id)).slice(0, TOP_RAPIDO)
    );
  };

  const guardar = async () => {
    setGuardando(true);
    setErrorGuardar('');
    try {
      const res = await axios.post(API_BASE_URL + FAVORITOS_GUARDAR, { sucursal_id: sucursalId, ids: seleccionVisible }, { headers: authHeader() });
      if (!res.data?.success) throw new Error(res.data?.message || 'No se pudieron guardar los favoritos.');
      onGuardado((res.data.data?.ids ?? []).map(Number));
    } catch (e) {
      setErrorGuardar(e.response?.data?.message || e.message || 'No se pudieron guardar los favoritos.');
      setGuardando(false);
    }
  };

  return (
    <Dialog open onClose={guardando ? undefined : onCerrar} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Star color="warning" />
        Elegir favoritos{sucursalNombre ? ` — ${sucursalNombre}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Tildá los productos que querés ver en la categoría <strong>Favoritos</strong>. Es la misma lista para todos los que
          cargan egresos en esta sucursal.
        </Typography>

        {errorRanking && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            No se pudo traer lo más vendido ({errorRanking}). Igual podés elegir a mano.
          </Alert>
        )}
        {errorGuardar && <Alert severity="error" sx={{ mb: 1.5 }}>{errorGuardar}</Alert>}
        {aviso && <Alert severity="info" sx={{ mb: 1.5 }}>{aviso}</Alert>}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
          <TextField
            size="small" placeholder="Buscar producto…" value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)} sx={{ flex: '1 1 180px' }}
          />
          <ToggleButtonGroup size="small" exclusive value={orden} onChange={(e, v) => v && setOrden(v)}>
            <ToggleButton value="vendidos">Más vendidos</ToggleButton>
            <ToggleButton value="az">A-Z</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
          <Button size="small" variant="outlined" startIcon={<Star />} onClick={elegirMasVendidos} disabled={cargando || ranking.length === 0}>
            Elegir los {TOP_RAPIDO} más vendidos
          </Button>
          <Chip size="small" color={seleccionVisible.length > 0 ? 'primary' : 'default'} label={`${seleccionVisible.length} elegido${seleccionVisible.length === 1 ? '' : 's'}`} />
        </Box>

        <Paper variant="outlined" sx={{ maxHeight: isMobile ? '48vh' : 360, overflow: 'auto' }}>
          {cargando ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={28} /></Box>
          ) : (
            <List dense disablePadding>
              {lista.length === 0 ? (
                <ListItemButton disabled><ListItemText secondary="Sin resultados." /></ListItemButton>
              ) : lista.map((p) => {
                const id = Number(p.id_club_grido);
                const elegido = seleccion.includes(id);
                const un = unidadesPorId.get(id);
                return (
                  <ListItemButton key={id} dense selected={elegido} onClick={() => alternar(id)}>
                    <Checkbox edge="start" checked={elegido} tabIndex={-1} disableRipple size="small" sx={{ mr: 1 }} />
                    <ListItemText
                      primary={p.art_descripcion}
                      secondary={errorRanking ? undefined : (un ? `${un} un. en el último mes` : 'Sin ventas en el último mes')}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Paper>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          "Último mes" = los últimos 30 días, contando solo los egresos no anulados de esta sucursal.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCerrar} disabled={guardando}>Cancelar</Button>
        <Button
          variant="contained" onClick={guardar} disabled={guardando || cargando}
          startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {guardando ? 'Guardando…' : 'Guardar favoritos'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default FavoritosDialog;
